import React, { useState } from "react";

import { Button, Icon, Input, Link, Modal, Select } from "@stellar/design-system";

import { USER_ROLES_ARRAY } from "@/constants/settings";
import { userRoleText } from "@/helpers/userRoleText";
import { UserRole } from "@/types";

export type InviteRow = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole | "";
  status: "idle" | "loading" | "error";
  errorMsg?: string;
};

export const newRow = (): InviteRow => ({
  id: Date.now() + Math.random(),
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  status: "idle",
});

export const canInviteRow = (row: InviteRow) =>
  Boolean(row.firstName && row.lastName && row.email && row.role);

const ROLES_PERMISSIONS = [
  {
    role: "Owner",
    description: "Full access",
    viewData: true,
    createDrafts: true,
    submit: true,
    manageUsers: true,
    technical: true,
  },
  {
    role: "Financial controller",
    description: "Can submit, no user management",
    viewData: true,
    createDrafts: true,
    submit: true,
    manageUsers: false,
    technical: false,
  },
  {
    role: "Approver",
    description: "Can approve and submit drafts",
    viewData: true,
    createDrafts: false,
    submit: true,
    manageUsers: false,
    technical: false,
  },
  {
    role: "Read only",
    description: "View data only",
    viewData: true,
    createDrafts: false,
    submit: false,
    manageUsers: false,
    technical: false,
  },
  {
    role: "Developer",
    description: "Technical access only",
    viewData: false,
    createDrafts: false,
    submit: false,
    manageUsers: false,
    technical: true,
  },
];

// Note: "Read only" maps to the backend "business" role.

interface Step3InviteTeammatesProps {
  inviteRows: InviteRow[];
  setInviteRows: React.Dispatch<React.SetStateAction<InviteRow[]>>;
  isLoading: boolean;
  canProceed: boolean;
  onNext: () => void;
  onSkip: () => void;
}

export const Step3InviteTeammates: React.FC<Step3InviteTeammatesProps> = ({
  inviteRows,
  setInviteRows,
  isLoading,
  canProceed,
  onNext,
  onSkip,
}) => {
  const [rolesModalVisible, setRolesModalVisible] = useState(false);

  const handleUpdateRow = (rowId: number, field: keyof InviteRow, value: string) => {
    setInviteRows((prev) =>
      prev.map((r) =>
        r.id === rowId ? { ...r, [field]: value, status: "idle", errorMsg: undefined } : r,
      ),
    );
  };

  const handleDeleteRow = (rowId: number) => {
    setInviteRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  const handleAddRow = () => {
    setInviteRows((prev) => [...prev, newRow()]);
  };

  return (
    <div className="Onboarding__card Onboarding__card--medium">
      <p className="Onboarding__cardTitle">Invite your teammates</p>
      <p className="Onboarding__cardDescription">
        New users will receive an email invitation to join your organization with the defined role
      </p>

      <Link
        size="sm"
        role="button"
        variant="primary"
        className="Onboarding__rolesLink"
        onClick={() => setRolesModalVisible(true)}
      >
        <Icon.InfoCircle />
        About the roles
      </Link>

      <div className="Onboarding__inviteRows">
        {inviteRows.map((row) => (
          <div key={row.id} className="Onboarding__inviteRow">
            <div className="Onboarding__inviteRows__smallField">
              <Input
                name="fname"
                fieldSize="md"
                label="First name"
                value={row.firstName}
                id={`fname-${row.id}`}
                disabled={row.status === "loading"}
                onChange={(e) => handleUpdateRow(row.id, "firstName", e.target.value)}
              />
            </div>
            <div className="Onboarding__inviteRows__smallField">
              <Input
                name="lname"
                fieldSize="md"
                label="Last name"
                value={row.lastName}
                id={`lname-${row.id}`}
                disabled={row.status === "loading"}
                onChange={(e) => handleUpdateRow(row.id, "lastName", e.target.value)}
              />
            </div>
            <div className="Onboarding__inviteRows__mediumField">
              <Input
                name="email"
                type="email"
                fieldSize="md"
                value={row.email}
                label="Email address"
                id={`email-${row.id}`}
                disabled={row.status === "loading"}
                onChange={(e) => handleUpdateRow(row.id, "email", e.target.value)}
              />
            </div>
            <div className="Onboarding__inviteRows__mediumField">
              <Select
                name="role"
                label="Role"
                fieldSize="md"
                value={row.role}
                id={`role-${row.id}`}
                disabled={row.status === "loading"}
                onChange={(e) => handleUpdateRow(row.id, "role", e.target.value)}
              >
                <option value=""></option>
                {USER_ROLES_ARRAY.filter((r) => r !== "initiator").map((r) => (
                  <option key={r} value={r}>
                    {userRoleText(r)}
                  </option>
                ))}
              </Select>
            </div>
            <Button
              size="md"
              variant="destructive"
              icon={<Icon.Trash01 />}
              aria-label="Remove row"
              title="Remove row"
              disabled={row.status === "loading" || inviteRows.length === 1}
              onClick={() => handleDeleteRow(row.id)}
            />
            {row.status === "error" && <p className="Onboarding__rowError">{row.errorMsg}</p>}
          </div>
        ))}
        <Button
          size="lg"
          variant="secondary"
          icon={<Icon.Plus />}
          onClick={handleAddRow}
          style={{ alignSelf: "flex-start" }}
        >
          Add Row
        </Button>
      </div>

      <div className="Onboarding__actions">
        <Button
          size="lg"
          onClick={onNext}
          variant="primary"
          isFullWidth={true}
          isLoading={isLoading}
          disabled={!canProceed}
        >
          Next
        </Button>
        <Link role="button" size="md" variant="secondary" onClick={onSkip}>
          Skip for now
        </Link>
      </div>

      <Modal visible={rolesModalVisible} onClose={() => setRolesModalVisible(false)}>
        <Modal.Body>
          <table className="Onboarding__rolesTable">
            <thead>
              <tr>
                <th>Role</th>
                <th>View data</th>
                <th>Create drafts</th>
                <th>Submit disbursements</th>
                <th>Manage users</th>
                <th>Technical access</th>
              </tr>
            </thead>
            <tbody>
              {ROLES_PERMISSIONS.map((r) => (
                <tr key={r.role}>
                  <td>
                    <strong>{r.role}</strong>
                    <br />
                    <span className="Onboarding__roleDesc">{r.description}</span>
                  </td>
                  {[r.viewData, r.createDrafts, r.submit, r.manageUsers, r.technical].map(
                    (has, i) => (
                      <td key={i}>
                        {has ? (
                          <span className="Onboarding__permCheck">
                            <Icon.Check />
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Modal.Body>
      </Modal>
    </div>
  );
};
